import { supabase } from '../lib/supabase';

// Helper function to convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  
  if (Array.isArray(obj)) {
    return obj?.map(item => toCamelCase(item));
  }
  
  const camelObj = {};
  for (const key in obj) {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = toCamelCase(obj?.[key]);
  }
  return camelObj;
}

export const notificationsHubService = {
  /**
   * Get all notifications for a user with optional filtering
   */
  async getNotifications(userId, filters = {}) {
    try {
      let query = supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false });

      // Apply category filter
      if (filters?.category && filters?.category !== 'all') {
        const categoryTypeMap = {
          'payment': 'payment_update',
          'offers': 'deal_status_change',
          'flights': 'system_alert',
          'disputes': 'dispute_filed',
          'admin': 'system_alert'
        };
        
        if (categoryTypeMap?.[filters?.category]) {
          query = query?.eq('type', categoryTypeMap?.[filters?.category]);
        }
      }

      // Apply read status filter
      if (filters?.readStatus === 'unread') {
        query = query?.eq('is_read', false);
      } else if (filters?.readStatus === 'read') {
        query = query?.eq('is_read', true);
      }

      // Apply date range filter
      if (filters?.dateFrom) {
        query = query?.gte('created_at', filters?.dateFrom);
      }
      if (filters?.dateTo) {
        query = query?.lte('created_at', filters?.dateTo);
      }

      // Apply priority filter (based on metadata)
      if (filters?.priority) {
        // This would require JSONB query if priority is in metadata
        // For now, we'll filter client-side
      }

      const { data, error } = await query;

      if (error) {
        console.error('Get notifications error:', error);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Get notifications error:', error);
      return [];
    }
  },

  /**
   * Get unread count by category
   */
  async getUnreadCountByCategory(userId) {
    try {
      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('type')
        ?.eq('user_id', userId)
        ?.eq('is_read', false);

      if (error) {
        console.error('Get unread count error:', error);
        return {};
      }

      const counts = {
        all: data?.length || 0,
        payment: 0,
        offers: 0,
        flights: 0,
        disputes: 0,
        admin: 0
      };

      data?.forEach(notification => {
        switch (notification?.type) {
          case 'payment_update':
            counts.payment++;
            break;
          case 'deal_status_change':
            counts.offers++;
            break;
          case 'dispute_filed':
            counts.disputes++;
            break;
          case 'system_alert':
            counts.admin++;
            counts.flights++; // System alerts can be flight-related
            break;
        }
      });

      return counts;
    } catch (error) {
      console.error('Get unread count error:', error);
      return { all: 0, payment: 0, offers: 0, flights: 0, disputes: 0, admin: 0 };
    }
  },

  /**
   * Mark single notification as read
   */
  async markAsRead(notificationId) {
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.eq('id', notificationId);

      if (error) {
        console.error('Mark as read error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Mark as read error:', error);
      return false;
    }
  },

  /**
   * Mark multiple notifications as read
   */
  async markMultipleAsRead(notificationIds) {
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.in('id', notificationIds);

      if (error) {
        console.error('Mark multiple as read error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Mark multiple as read error:', error);
      return false;
    }
  },

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId) {
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.eq('user_id', userId)
        ?.eq('is_read', false);

      if (error) {
        console.error('Mark all as read error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Mark all as read error:', error);
      return false;
    }
  },

  /**
   * Delete single notification
   */
  async deleteNotification(notificationId) {
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.delete()
        ?.eq('id', notificationId);

      if (error) {
        console.error('Delete notification error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Delete notification error:', error);
      return false;
    }
  },

  /**
   * Delete multiple notifications
   */
  async deleteMultiple(notificationIds) {
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.delete()
        ?.in('id', notificationIds);

      if (error) {
        console.error('Delete multiple error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Delete multiple error:', error);
      return false;
    }
  },

  /**
   * Delete old read notifications (older than specified days)
   */
  async deleteOldNotifications(userId, daysOld = 30) {
    try {
      const cutoffDate = new Date();
      cutoffDate?.setDate(cutoffDate?.getDate() - daysOld);

      const { error } = await supabase
        ?.from('notifications')
        ?.delete()
        ?.eq('user_id', userId)
        ?.eq('is_read', true)
        ?.lt('created_at', cutoffDate?.toISOString());

      if (error) {
        console.error('Delete old notifications error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Delete old notifications error:', error);
      return false;
    }
  },

  /**
   * Subscribe to real-time notifications
   */
  subscribeToNotifications(userId, callback) {
    const channel = supabase
      ?.channel(`notifications_hub_${userId}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          callback(toCamelCase(payload?.new));
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from notifications
   */
  unsubscribe(channel) {
    if (channel) {
      supabase?.removeChannel(channel);
    }
  }
};